import EyeIcon from '@betfan/icons/linear/Security/Eye.svg?react';
import EyeClosedIcon from '@betfan/icons/linear/Security/EyeClosed.svg?react';
import cn from 'classnames';
import pick from 'lodash/pick';
import React, { ComponentType, PropsWithChildren, ReactElement, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import './DSInputStyles.scss';
import { helpText } from './utils';

export type DSInputColor = 'green' | 'theme';

type BaseProps = Pick<React.HTMLAttributes<HTMLDivElement>, 'className'> & {
	color?: DSInputColor;
	rightIcon?: ComponentType<any> | ReactElement;
	label: string;
	value: string;
	error?: string;
	helpText?: string;
	autoFocus?: boolean;
	disabled?: boolean;
	onChange: (newValue: string) => void;
	cyTestRef?: string;
	cyErrorRef?: string;
} & (
		| {
				type?: 'text' | 'password' | 'tel';
				pattern: string;
		  }
		| {
				type?: 'text' | 'password' | 'email' | 'number' | 'textarea';
				pattern?: undefined;
		  }
	) &
	// rows can be only be provided when input type is textarea
	({ type: 'textarea'; rows?: number } | { type?: 'text' | 'password' | 'tel' | 'email' | 'number'; rows?: undefined });
type DSInputProps = BaseProps;

export const DSInput = ({ color = 'theme', rows = 4, ...props }: PropsWithChildren<DSInputProps>) => {
	const [passwordShown, setPasswordShown] = useState<boolean>(false);

	const classes = () =>
		cn(
			{
				'text-error-500 outline outline-2 outline-error-500': props.error,
				'ds-input': !props.error,
			},
			'relative',
			'rounded-4',
			{
				'bg-primary-500 text-gray-200 dark:bg-dark-300 dark:text-gray-300': color === 'green',
				'bg-gray-100 text-gray-700 dark:bg-dark-300 dark:text-gray-300': color === 'theme',
			},
			props.className,
		);

	const iconClasses = () =>
		cn(
			'absolute right-3 top-1/2 -translate-y-1/2',
			'w-6 h-6',
			props.error
				? 'text-error-500'
				: {
						'text-gray-100': color === 'green',
						'text-gray-700 dark:text-gray-300': color === 'theme',
				  },
		);

	const inputClasses = () =>
		cn(
			'peer',
			'appearance-none bg-transparent w-full h-full',
			'focus:outline-none',
			'pl-3 pt-7 pb-2',
			'placeholder-transparent placeholder:select-none', // Hide placeholder to replace it with a regular div but keep its value to be able to use :placeholder-shown
			props.rightIcon || props.type === 'password' ? 'pr-12' : 'pr-3',
			{
				'opacity-40': props.disabled,
			},
			{ 'resize-none': props.type === 'textarea' },
			color === 'green' ? 'text-white' : '',
		);

	const labelClasses = () =>
		cn(
			'transition-all',
			'pointer-events-none', // To allow for focusing the input by clicking on the label
			'select-none',
			'absolute left-3',
			'peer-focus:top-4 peer-[:not(:placeholder-shown)]:top-4',
			props.type === 'textarea' ? 'top-7' : 'top-1/2 -translate-y-1/2',
			'text-paragraph-md peer-focus:text-label-xs peer-[:not(:placeholder-shown)]:text-label-xs',
			'font-medium       peer-focus:font-normal   peer-[:not(:placeholder-shown)]:font-normal',
		);

	const icon = () => {
		if (props.rightIcon) {
			return <div className={iconClasses()}>{React.isValidElement(props.rightIcon) ? props.rightIcon : <props.rightIcon />}</div>;
		}

		if (props.type === 'password') {
			if (passwordShown) {
				return (
					<button type="button" className={iconClasses()} onClick={() => setPasswordShown(false)}>
						<EyeClosedIcon />
					</button>
				);
			} else {
				return (
					<button type="button" className={iconClasses()} onClick={() => setPasswordShown(true)}>
						<EyeIcon />
					</button>
				);
			}
		}
	};

	const type = () => {
		if (!props.type) {
			return 'text';
		}

		if (props.type !== 'password') {
			return props.type;
		}

		if (passwordShown) {
			return 'text';
		}

		return 'password';
	};

	const commonProps = {
		autoFocus: props.autoFocus,
		className: inputClasses(),
		placeholder: props.label,
		value: props.value,
		onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => props.onChange?.(event.target.value),
		disabled: props.disabled,
		'data-cy': props.cyTestRef,
	};

	const inputComponent = () => {
		if (props.type === 'textarea') {
			return <textarea {...commonProps} rows={rows} />;
		}

		if (props.pattern) {
			return <PatternFormat format={props.pattern} allowEmptyFormatting type={props.type} {...commonProps} />;
		}

		return <input type={type()} {...commonProps} />;
	};

	return (
		<div>
			<div className={classes()}>
				{inputComponent()}

				<div className={labelClasses()}>{props.label}</div>

				{icon()}
			</div>

			{helpText({ color, ...pick(props, 'error', 'helpText', 'disabled') })}
		</div>
	);
};
